import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import SplitterPaneViewModelType from '@/ViewModels/Controls/SplitterPaneViewModel';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class SplitterPane extends Control {

    private jQueryControl: JQuery;

    private readonly sizeProperty: KnockoutObservable<string>;
    private readonly minSizeCssProperty: KnockoutObservable<string>;
    private readonly maxSizeCssProperty: KnockoutObservable<string>;

    public constructor(public viewModel: SplitterPaneViewModelType, private element: JQuery) {
        super(viewModel, element);

        if (!element.length)
            throw new Error('Element needed for splitter pane!');

        this.sizeProperty = this.defineComputed(() => viewModel.isHorizontal() ? 'width' : 'height');
        this.minSizeCssProperty = this.defineComputed(() => `min-${this.sizeProperty()}`);
        this.maxSizeCssProperty = this.defineComputed(() => `max-${this.sizeProperty()}`);
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element;

        this.applyBindings(this.jQueryControl, {
            controlName: 'splitterpane',
            locator: this.viewModel.locator,
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            visible: this.defineComputed(() => {
                return this.viewModel.isVisible() && this.viewModel.isExpanded();
            }),
            states: this.definePureComputed({
                read: () => {
                    return [
                        this.viewModel.isEnabled() ? 'enabled' : 'disabled',
                        this.viewModel.isExpanded() ? 'expanded' : 'collapsed'
                    ];
                }
            }),
            styles: this.definePureComputed({
                read: () => {
                    const styles: string[] = [];
                    if (this.viewModel.isScrollable())
                        styles.push('scrollable');
                    return styles;
                }
            }),
        });

        this.bindSize();
        this.bindMinSize();
        this.bindMaxSize();
        this.bindResizeOverlay();

        return this.jQueryControl;
    }

    private bindSize() {
        const sizeComputed: KnockoutComputed<[string, string | number]> = this.defineComputed(() => {
            const property = this.sizeProperty();
            const isExpanded = this.viewModel.isExpanded();
            const isVisible = this.viewModel.isVisible();
            const size = this.viewModel.size();
            const collapsedSize = this.viewModel.collapsedSize();
            return [property, isVisible && isExpanded ? size : collapsedSize];
        });
        this.defineSubscription(sizeComputed, ([property, size]) => {
            // Async, damit das Rendering des Controls abgeschlossen ist
            this.async(() => {
                if (this.isDisposed || this.viewModel.isDisposed)
                    return;

                this.jQueryControl.css({
                    'flex-basis': typeof size === 'number' ? `${size}px` : size,
                    // Ist eine Größe definiert, soll diese auch eingehalten werden.
                    // Das Element darf also nicht kleiner oder größer werden.
                    'flex-shrink': size != undefined ? 0 : 1,
                    'flex-grow': size != undefined ? 0 : 1,
                });
                this.viewModel.calculatedSize(this.viewModel.isHorizontal() ? this.jQueryControl.width() : this.jQueryControl.height());
            });
        })(sizeComputed());
    }

    private bindMinSize() {
        const minSizeComputed: KnockoutComputed<[string, string | number]> = this.defineComputed(() => {
            const property = this.minSizeCssProperty();
            const isExpanded = this.viewModel.isExpanded();
            const minSize = this.viewModel.min();
            return [property, isExpanded ? minSize : null];
        });
        this.defineSubscription(minSizeComputed, ([property, min]) => {
            this.resetMinMaxSize();
            this.jQueryControl.css(property, min);
        })(minSizeComputed());
    }

    private bindMaxSize() {
        const maxSizeComputed: KnockoutComputed<[string, string | number]> = this.defineComputed(() => {
            const property = this.maxSizeCssProperty();
            const isExpanded = this.viewModel.isExpanded();
            const maxSize = this.viewModel.max();
            return [property, isExpanded ? maxSize : null];
        });
        this.defineSubscription(maxSizeComputed, ([property, max]) => {
            this.resetMinMaxSize();
            this.jQueryControl.css(property, max);
        })(maxSizeComputed());
    }

    private resetMinMaxSize() {
        this.jQueryControl.css('min-height', 0);
        this.jQueryControl.css('min-width', 0);
    }

    private bindResizeOverlay() {
        if (!this.viewModel.useResizeOverlay)
            return;

        this.defineSubscription(this.viewModel.showResizeOverlay, show => {
            if (this.isDisposedOrDisposing())
                return;

            const overlayClass = 'resize-overlay' as const;
            const $children = this.jQueryControl.children(`.${overlayClass}`);

            if (show) {
                if (!$children.length) {
                    const $overlay = $('<div>').addClass(overlayClass).css({
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        inset: '0',
                    });
                    this.jQueryControl.append($overlay);
                }
            } else {
                $children.remove();
            }
        });
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('SplitterPane', SplitterPane, 'RADPresentation.HTMLClient.DefaultViewExtensions');